import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";

const CartContext = createContext(null);

export function tierFor(qty, tiers) {
  const sorted = [...(tiers || [])].sort((a, b) => a.min - b.min);
  let match = null;
  for (const t of sorted) {
    if (qty >= t.min) match = t;
  }
  return match;
}

export function unitPriceCents(baseCents, qty, tiers) {
  const tier = tierFor(qty, tiers);
  const d = tier ? tier.discount_pct : 0;
  return Math.round((baseCents * (100 - d)) / 100);
}

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then(({ data }) => setProducts(data.products || []))
      .catch(() => setProducts([]))
      .finally(() => setProductsLoaded(true));
  }, []);

  const addItem = (product, qty = 1) => {
    const item = {
      id: product.product_id,
      name: product.name,
      size: product.size,
      sku: product.sku,
      image: product.image,
      baseCents: product.base_price_cents,
      tiers: product.bulk_tiers || [],
    };
    setItems((prev) => {
      const existing = prev.find((i) => i.id === item.id);
      if (existing) {
        return prev.map((i) =>
          i.id === item.id ? { ...i, qty: Math.min(i.qty + qty, 99) } : i
        );
      }
      return [...prev, { ...item, qty }];
    });
    setOpen(true);
  };

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));

  const updateQty = (id, qty) => {
    if (qty < 1) return removeItem(id);
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, qty: Math.min(qty, 99) } : i))
    );
  };

  const clear = () => setItems([]);

  const value = useMemo(() => {
    const count = items.reduce((s, i) => s + i.qty, 0);
    const total = items.reduce((s, i) => s + i.qty * unitPriceCents(i.baseCents, i.qty, i.tiers), 0) / 100;
    return { items, addItem, removeItem, updateQty, clear, open, setOpen, count, total, products, productsLoaded };
  }, [items, open, products, productsLoaded]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return useContext(CartContext);
}
